/**
 * Analytics Manager
 *
 * Handles all analytics event tracking for the Quickstart flow.
 * Centralizes analytics logic and provides type-safe event tracking.
 *
 * This is feature-specific to Quickstart and knows about:
 * - Quickstart phases
 * - Quickstart theme/config
 * - UTM parameters
 * - Session timing
 */

import { sendAnalytics } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../../api';
import type { QuickstartState } from '../QuickstartCoordinator';

export interface AnalyticsManagerConfig {
  /**
   * Get current state (for base analytics)
   */
  getState: () => QuickstartState;
}

/**
 * Manager for Quickstart analytics tracking
 */
export class AnalyticsManager {
  private config: AnalyticsManagerConfig;

  constructor(config: AnalyticsManagerConfig) {
    this.config = config;
  }

  /**
   * Get base analytics properties included in all events
   */
  private getBaseProperties() {
    const state = this.config.getState();
    const persistentId = getPersistentPlayerId();

    return {
      persistentId,
      quickstartSlug: state.theme.slug,
      flow: 'quickstart' as const,
      tier2Filters: ['deaddrop'] as ['deaddrop'],
    };
  }

  /**
   * Get playerAuthId if the user is authenticated
   */
  private getPlayerAuthId(): string | undefined {
    const playerAuthId = localStorage.getItem('clerkUserID');
    return playerAuthId || undefined;
  }

  public sendEnvironmentCheckCompleted(): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Environment Check Completed',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      isEmbeddedBrowser: state.environment.isEmbedded,
    });
  }

  public sendEmbeddedBrowserBlocked(): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Embedded Browser Blocked',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      browserName: state.environment.browserName,
    });
  }

  public sendOpenInBrowserClicked(browserName: string): void {
    sendAnalytics({
      name: 'Open In Browser Clicked',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      browserName,
    });
  }

  public sendMatchmakingStarted(matchmakingTimeout: number): void {
    sendAnalytics({
      name: 'Matchmaking Started',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      matchmakingTimeout,
    });
  }

  public sendMatchmakingFailed(reason: string, duration: number): void {
    sendAnalytics({
      name: 'Matchmaking Failed',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      reason,
      duration,
    });
  }

  public sendMatchmakingSucceeded(): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Matchmaking Succeeded',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '', // matchId is the invitationId
      broadcastId: state.broadcastId ?? '',
      duration: state.matchmakingDuration ?? 0,
    });
  }

  public sendBroadcastJoinAttempted(): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Broadcast Join Attempted',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '',
      broadcastId: state.broadcastId ?? '',
    });
  }

  public sendBroadcastJoinFailed(
    reason: string,
    duration: number,
    errorStage?: 'signaling' | 'sdp' | 'ice' | 'dtls' | 'auth' | 'timeout' | 'unknown'
  ): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Broadcast Join Failed',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '',
      broadcastId: state.broadcastId ?? '',
      reason,
      errorStage,
      duration,
    });
  }

  public sendBroadcastJoinSucceeded(duration: number): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Player Joined Broadcast',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: localStorage.getItem('clerkUserID') ?? '',
      playerConnectionId: state.playerConnectionId ?? '',
      inviteId: state.matchId ?? '', // Using matchId as inviteId for quickstart
      broadcastSessionId: state.broadcastId ?? '',
      broadcastId: state.broadcastId ?? '',
      duration,
    });
  }

  public sendPlayerLeftBroadcast(): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Player Left Broadcast',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      playerConnectionId: state.playerConnectionId ?? '',
      inviteId: state.matchId ?? '',
      broadcastSessionId: state.broadcastId ?? '',
    });
  }

  public sendAFKTimerStarted(timeFromJoinMs: number): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'AFK Timer Started',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '',
      broadcastId: state.broadcastId ?? '',
      timeoutSeconds: state.afkTimeoutSeconds,
      timeFromJoinMs,
    });
  }

  public sendAFKTimeoutReached(sessionDuration: number): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'AFK Timeout Reached',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '',
      broadcastId: state.broadcastId ?? '',
      afkTimeoutSeconds: state.afkTimeoutSeconds,
      sessionDuration,
    });
  }

  public sendFirstInputDetected(
    timeToFirstInput: number,
    inputDeviceType?: 'keyboard' | 'mouse' | 'touch' | 'gamepad'
  ): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'First Input Detected',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '',
      broadcastId: state.broadcastId ?? '',
      timeToFirstInput,
      inputDeviceType,
    });
  }

  public sendAdTimerStarted(): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Ad Timer Started',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '',
      broadcastId: state.broadcastId ?? '',
      durationSeconds: state.adTimerSeconds,
    });
  }

  public sendAdTimerExpired(totalPlaytime: number): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Ad Timer Expired',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '',
      broadcastId: state.broadcastId ?? '',
      totalPlaytime,
    });
  }

  public sendWishlistClicked(
    playtime: number,
    uiVariant?: string,
    buttonPosition?: string,
    elapsedSinceTimerStartSeconds?: number
  ): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Wishlist Clicked',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? '',
      broadcastId: state.broadcastId ?? '',
      playtime,
      uiVariant,
      buttonPosition,
      elapsedSinceTimerStartSeconds,
    });
  }

  public sendCTAPageViewed(
    totalSessionDuration: number,
    ctaType?: string,
    uiVariant?: string,
    referrerPage?: string
  ): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'CTA Page Viewed',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? undefined,
      broadcastId: state.broadcastId ?? undefined,
      exitReason: state.exitReason ?? 'manual_exit',
      totalSessionDuration,
      ctaType,
      uiVariant,
      referrerPage,
    });
  }

  public sendWishlistCTAClicked(
    steamUrl: string,
    providerInferred?: string,
    providerSupported?: boolean,
    providerSelected?: string,
    dedupRuleApplied?: boolean
  ): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Wishlist CTA Clicked',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: this.getPlayerAuthId(),
      invitationId: state.matchId ?? undefined,
      broadcastId: state.broadcastId ?? undefined,
      steamUrl,
      providerInferred,
      providerSupported,
      providerSelected,
      dedupRuleApplied,
    });
  }

  public sendAuthAttempted(): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Auth Attempted',
      role: 'player',
      ...this.getBaseProperties(),
      invitationId: state.matchId ?? undefined,
      broadcastId: state.broadcastId ?? undefined,
    });
  }

  public sendAuthFailed(
    authProvider: string,
    reason: string,
    attemptId?: string,
    failureReason?: 'denied' | 'closed' | 'network' | 'provider_error' | 'timeout' | 'unknown',
    errorCode?: string,
    errorMessage?: string
  ): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Auth Failed',
      role: 'player',
      ...this.getBaseProperties(),
      invitationId: state.matchId ?? undefined,
      broadcastId: state.broadcastId ?? undefined,
      authProvider,
      reason,
      attemptId,
      failureReason,
      errorCode,
      errorMessage,
    });
  }

  public sendAuthSucceeded(
    authProvider: string,
    playerAuthId: string,
    attemptId?: string,
    accountState?: 'signup' | 'login'
  ): void {
    sendAnalytics({
      name: 'User Authenticated',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId,
      source: 'browser',
      authProvider,
      attemptId,
      accountState,
    });
  }

  public sendThankYouPageViewed(playerAuthId: string, uiVariant?: string): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Thank You Page Viewed',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId,
      invitationId: state.matchId ?? undefined,
      broadcastId: state.broadcastId ?? undefined,
      uiVariant,
    });
  }

  public sendWishlistEmailSent(playerAuthId: string, email: string): void {
    console.log('[AnalyticsManager] Wishlist email sent', { playerAuthId, email });
    sendAnalytics({
      name: 'Wishlist Emailed',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId,
      hostAuthId: playerAuthId, // Legacy field for backward compatibility
      email,
    });
  }

  public sendDeveloperLogoClicked(
    destinationId: string,
    openBehavior: 'new_tab' | 'same_tab',
    playerAuthId?: string
  ): void {
    const state = this.config.getState();
    sendAnalytics({
      name: 'Developer Logo Clicked',
      role: 'player',
      ...this.getBaseProperties(),
      playerAuthId: playerAuthId ?? this.getPlayerAuthId(),
      invitationId: state.matchId ?? undefined,
      broadcastId: state.broadcastId ?? undefined,
      destinationId,
      openBehavior,
    });
  }

  public sendThankYouRedirect(steamUrl: string, userAuthId?: string): void {
    sendAnalytics({
      name: 'Thank You Redirect',
      role: 'player',
      ...this.getBaseProperties(),
      steamUrl,
      userAuthId,
    });
  }

  /**
   * No cleanup needed for analytics
   */
  public cleanup(): void {
    // No-op
  }
}
